// ── BCH Live — Stream Overlays ────────────────────────────────────
//
// Reusable overlay components rendered over the player. Each one is
// individually toggleable by the creator (see OVERLAY_CONFIGS and
// getOverlayPrefs in lib/liveStream.ts).
//
//   • RecentTipsOverlay      — latest BCH tips
//   • TopSupporterOverlay    — biggest supporter of the session
//   • GoalProgressOverlay    — donation goal progress bar
//   • DonationTicker         — scrolling supporter ticker
//   • FollowerAlertOverlay   — transient new-follower pop
//   • SuperChatHighlight     — large animated card for big tips
//
// All amounts are BCH-native (sats), displayed in USD for familiarity.

import React, { useEffect, useMemo, useState } from 'react';
import { Coins, Trophy, Target, UserPlus, Sparkles } from 'lucide-react';
import { useNostrProfile } from '../../hooks/useNostrQuery';
import type { OverlayPrefs } from '../../lib/liveStream';

// ── Shared helpers ────────────────────────────────────────────────

export function formatUsd(usd: number): string {
  if (usd >= 1) return `$${usd.toFixed(usd % 1 === 0 ? 0 : 2)}`;
  return `$${usd.toFixed(2)}`;
}

/** Tier colour by USD size — mirrors the Super Chat tiers. */
export function tierColor(usd: number): string {
  if (usd >= 10) return '#f59e0b';
  if (usd >= 5) return '#ef4444';
  if (usd >= 1) return '#8b5cf6';
  if (usd >= 0.25) return '#3b82f6';
  return '#10b981';
}

export interface TipEntry {
  id: string;
  pubkey: string;
  usd: number;
  sats: number;
  message: string;
  created_at: number;
}

function NameInline({ pubkey }: { pubkey: string }) {
  const { profile } = useNostrProfile(pubkey);
  return <>{profile?.display_name || profile?.name || pubkey.slice(0, 8)}</>;
}

function AvatarInline({ pubkey, size = 20 }: { pubkey: string; size?: number }) {
  const { profile } = useNostrProfile(pubkey);
  const style = { width: size, height: size };
  return profile?.picture
    ? <img src={profile.picture} alt="" style={style} className="rounded-full object-cover" />
    : <div style={style} className="rounded-full bg-white/20" />;
}

// ══════════════════════════════════════════════════════════════════
//  RECENT TIPS
// ══════════════════════════════════════════════════════════════════

export function RecentTipsOverlay({ tips }: { tips: TipEntry[] }) {
  const recent = tips.slice(0, 4);
  if (recent.length === 0) return null;
  return (
    <div className="pointer-events-none absolute left-3 top-3 z-20 flex max-w-[60%] flex-col gap-1.5">
      {recent.map(t => (
        <div
          key={t.id}
          className="flex items-center gap-2 rounded-lg bg-black/70 px-2 py-1 text-[11px] text-white shadow-lg backdrop-blur-sm"
          style={{ borderLeft: `3px solid ${tierColor(t.usd)}` }}
        >
          <AvatarInline pubkey={t.pubkey} size={18} />
          <span className="max-w-[90px] truncate font-bold"><NameInline pubkey={t.pubkey} /></span>
          <span className="ml-auto flex items-center gap-1 font-black" style={{ color: tierColor(t.usd) }}>
            <Coins className="h-3 w-3" /> {formatUsd(t.usd)}
          </span>
        </div>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  TOP SUPPORTER
// ══════════════════════════════════════════════════════════════════

export function TopSupporterOverlay({ tips }: { tips: TipEntry[] }) {
  const top = useMemo(() => {
    const byPk = new Map<string, number>();
    for (const t of tips) byPk.set(t.pubkey, (byPk.get(t.pubkey) || 0) + t.usd);
    const sorted = [...byPk.entries()].sort((a, b) => b[1] - a[1]);
    return sorted[0] || null;
  }, [tips]);

  if (!top) return null;
  const [pubkey, usd] = top;

  return (
    <div className="pointer-events-none absolute right-3 top-3 z-20">
      <div className="flex items-center gap-2 rounded-xl bg-black/75 px-2.5 py-1.5 shadow-lg ring-1 ring-amber-400/40 backdrop-blur-sm">
        <Trophy className="h-3.5 w-3.5 text-amber-400" />
        <div className="flex items-center gap-1.5">
          <AvatarInline pubkey={pubkey} size={20} />
          <div className="leading-tight">
            <p className="text-[9px] font-bold uppercase tracking-wider text-amber-400/80">Top supporter</p>
            <p className="max-w-[110px] truncate text-[11px] font-bold text-white">
              <NameInline pubkey={pubkey} />
            </p>
          </div>
          <span className="ml-1 rounded-md bg-amber-400 px-1.5 py-0.5 text-[10px] font-black text-amber-950">
            {formatUsd(usd)}
          </span>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  GOAL PROGRESS
// ══════════════════════════════════════════════════════════════════

export function GoalProgressOverlay({
  raisedUsd, targetUsd, title,
}: { raisedUsd: number; targetUsd: number; title?: string }) {
  if (!targetUsd || targetUsd <= 0) return null;
  const pct = Math.min(100, Math.round((raisedUsd / targetUsd) * 100));
  const complete = pct >= 100;

  return (
    <div className="pointer-events-none absolute inset-x-3 bottom-16 z-20 sm:inset-x-auto sm:left-3 sm:w-72">
      <div className="rounded-xl bg-black/75 p-2.5 shadow-lg backdrop-blur-sm">
        <div className="mb-1.5 flex items-center gap-1.5">
          <Target className="h-3.5 w-3.5 text-emerald-400" />
          <span className="truncate text-[11px] font-bold text-white">{title || 'Donation goal'}</span>
          <span className="ml-auto text-[11px] font-black text-emerald-400">
            {formatUsd(raisedUsd)} <span className="text-white/50">/ {formatUsd(targetUsd)}</span>
          </span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-white/15">
          <div
            className={`h-full rounded-full transition-all duration-700 ${
              complete
                ? 'bg-gradient-to-r from-amber-400 to-yellow-300'
                : 'bg-gradient-to-r from-emerald-500 to-teal-400'
            }`}
            style={{ width: `${Math.max(2, pct)}%` }}
          />
        </div>
        <p className="mt-1 text-right text-[10px] font-bold text-white/60">
          {complete ? 'Goal reached! 🎉' : `${pct}%`}
        </p>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  DONATION TICKER
// ══════════════════════════════════════════════════════════════════

export function DonationTicker({ tips }: { tips: TipEntry[] }) {
  if (tips.length === 0) return null;
  // Duplicated track gives a seamless loop.
  const items = [...tips.slice(0, 12), ...tips.slice(0, 12)];
  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 overflow-hidden bg-black/70 py-1 backdrop-blur-sm">
      <div className="ticker-track flex items-center gap-6 whitespace-nowrap">
        {items.map((t, i) => (
          <span key={`${t.id}-${i}`} className="flex items-center gap-1.5 text-[11px] text-white">
            <Coins className="h-3 w-3 text-emerald-400" />
            <span className="font-bold"><NameInline pubkey={t.pubkey} /></span>
            <span className="font-black" style={{ color: tierColor(t.usd) }}>{formatUsd(t.usd)}</span>
            {t.message && <span className="max-w-[220px] truncate text-white/70">“{t.message}”</span>}
          </span>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  FOLLOWER ALERT
// ══════════════════════════════════════════════════════════════════

export function FollowerAlertOverlay({
  pubkey, onDone,
}: { pubkey: string | null; onDone: () => void }) {
  useEffect(() => {
    if (!pubkey) return;
    const t = setTimeout(onDone, 5000);
    return () => clearTimeout(t);
  }, [pubkey, onDone]);

  if (!pubkey) return null;

  return (
    <div className="pointer-events-none absolute left-1/2 top-4 z-30 -translate-x-1/2">
      <div className="sc-enter flex items-center gap-2 rounded-xl bg-gradient-to-r from-primary to-emerald-500 px-3 py-2 shadow-2xl">
        <UserPlus className="h-4 w-4 text-white" />
        <AvatarInline pubkey={pubkey} size={22} />
        <span className="text-xs font-bold text-white">
          <NameInline pubkey={pubkey} /> just followed!
        </span>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  SUPER CHAT HIGHLIGHT
// ══════════════════════════════════════════════════════════════════

export function SuperChatHighlight({
  tip, onDone,
}: { tip: TipEntry | null; onDone: () => void }) {
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (!tip) return;
    setLeaving(false);
    // Bigger tips linger longer — same ladder as the pinned Super Chats.
    const dur = tip.usd >= 10 ? 10000 : tip.usd >= 5 ? 8000 : 6000;
    const fade = setTimeout(() => setLeaving(true), dur - 600);
    const done = setTimeout(onDone, dur);
    return () => { clearTimeout(fade); clearTimeout(done); };
  }, [tip, onDone]);

  if (!tip) return null;
  const color = tierColor(tip.usd);

  return (
    <div className="pointer-events-none absolute inset-x-0 top-1/4 z-30 flex justify-center px-4">
      <div
        className={`sc-enter w-full max-w-sm rounded-2xl p-3 shadow-2xl ${leaving ? 'sc-pin-out' : ''}`}
        style={{ backgroundColor: `${color}f0`, boxShadow: `0 0 40px -8px ${color}` }}
      >
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-white" />
          <AvatarInline pubkey={tip.pubkey} size={26} />
          <span className="truncate text-sm font-black text-white"><NameInline pubkey={tip.pubkey} /></span>
          <span className="ml-auto rounded-lg bg-black/25 px-2 py-0.5 text-sm font-black text-white">
            {formatUsd(tip.usd)}
          </span>
        </div>
        {tip.message && (
          <p className="mt-1.5 break-words text-sm font-medium text-white/95">{tip.message}</p>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
//  COMPOSITE LAYER
// ══════════════════════════════════════════════════════════════════

export interface OverlayLayerProps {
  prefs: OverlayPrefs;
  tips: TipEntry[];
  goal?: { raisedUsd: number; targetUsd: number; title?: string } | null;
  newFollower?: string | null;
  onFollowerDone?: () => void;
  highlightTip?: TipEntry | null;
  onHighlightDone?: () => void;
}

/**
 * Renders the enabled overlays over the player. Positioned absolutely
 * inside the player shell; pointer-events are disabled throughout so
 * overlays never swallow player clicks.
 */
export default function OverlayLayer({
  prefs, tips, goal, newFollower, onFollowerDone, highlightTip, onHighlightDone,
}: OverlayLayerProps) {
  return (
    <>
      {prefs.recentTips && <RecentTipsOverlay tips={tips} />}
      {prefs.topSupporter && <TopSupporterOverlay tips={tips} />}
      {prefs.goalProgress && goal && (
        <GoalProgressOverlay raisedUsd={goal.raisedUsd} targetUsd={goal.targetUsd} title={goal.title} />
      )}
      {prefs.donationTicker && <DonationTicker tips={tips} />}
      {prefs.followerAlerts && (
        <FollowerAlertOverlay pubkey={newFollower || null} onDone={onFollowerDone || (() => {})} />
      )}
      {prefs.superChatHighlight && (
        <SuperChatHighlight tip={highlightTip || null} onDone={onHighlightDone || (() => {})} />
      )}
    </>
  );
}
